import { required, SchemaPath, validate } from '@angular/forms/signals'

/**
 * Strasse und Hausnummer getrennt (seit 2026-10, Backend `docs/claude/adressen.md`).
 *
 * Bis dahin war die Strasse EIN Freitextfeld „Strasse und Hausnummer“, und Partner wie Hartmann,
 * Sabeda, RZH und eBrief rieten die Hausnummer per Regex. Am 2026-10-01 blieben so 21
 * Hartmann-Bestellungen haengen. Seitdem speichert das Backend `streetName` und `houseNumber`
 * getrennt und haelt `street` als ganze Zeile daneben: ein Frontend-Stand von vorher liest und
 * schreibt weiter nur `street`.
 *
 * Darum gilt fuer jedes Formular:
 * - **Laden** ueber {@link strasseUndHausnummerAus}: gespeicherte Teile, sonst die ganze Zeile im
 *   Strassenfeld (Altfall ohne erkennbare Hausnummer, das Formular verlangt sie dann).
 * - **Senden** ueber {@link alsAdressfelder}: `street` UND die beiden Teile. Ohne `street` speicherte
 *   ein Backend-Stand von vorher die Strasse gar nicht.
 * - **Pruefen** ueber {@link strasseHausnummerRegeln}: die Hausnummer ist Pflicht, sobald eine Strasse
 *   eingetragen ist (Entscheidung 2026-10-01, ohne Ausnahme). Ohne sie liefert das Backend nicht
 *   und schickt keinen Brief.
 *
 * Die Anzeige liest weiter `street`: das Backend haelt die Zeile bei jedem Schreiben aktuell.
 */

export const HAUSNUMMER_PFLICHT_MELDUNG = 'Bitte eintragen.'
export const HAUSNUMMER_IM_STRASSENFELD_MELDUNG = 'Die Hausnummer gehört ins Feld daneben.'

/** Die Teile, wie ein Formular sie fuehrt. Leere Felder sind `''`, nie `null`. */
export type StrasseUndHausnummer = { streetName: string; houseNumber: string }

/** Was eine Adresse vom Backend an Strasse traegt (`Address`, `AddressUiDto`). */
export type StrasseAusBackend = { street?: string | null; streetName?: string | null; houseNumber?: string | null }

/**
 * Formularwerte aus einer gespeicherten Adresse. Fehlen die getrennten Teile (Altdaten ohne erkennbare
 * Hausnummer, Antwort eines Backend-Stands von vorher), steht die ganze Zeile im Strassenfeld und die
 * Hausnummer bleibt leer.
 */
export function strasseUndHausnummerAus(adresse: StrasseAusBackend | null | undefined): StrasseUndHausnummer {
  if (adresse?.streetName) {
    return { streetName: adresse.streetName, houseNumber: adresse.houseNumber ?? '' }
  }
  return { streetName: adresse?.street ?? '', houseNumber: '' }
}

/** „Strasse Hausnummer“ als eine Zeile, so wie das Backend `street` zusammensetzt. */
export function strassenzeile(streetName: string | null | undefined, houseNumber: string | null | undefined): string {
  return [streetName?.trim(), houseNumber?.trim()].filter((teil) => !!teil).join(' ')
}

/**
 * Was an Strasse ans Backend geht: die ganze Zeile UND die Teile. Leere Werte gehen als `undefined`,
 * wie die uebrigen Adressfelder.
 */
export function alsAdressfelder(teile: StrasseUndHausnummer): { street?: string; streetName?: string; houseNumber?: string } {
  const strasse = (teile.streetName ?? '').trim()
  const hausnummer = (teile.houseNumber ?? '').trim()
  return {
    street: strassenzeile(strasse, hausnummer) || undefined,
    streetName: strasse || undefined,
    houseNumber: hausnummer || undefined,
  }
}

/**
 * Steht am Ende des Strassenfelds eine Hausnummer? „Lindenstraße 21a“, „Hauptstr.15“. Eine Zahl
 * im Namen („Straße des 17. Juni“, „Q7“) zaehlt nicht.
 */
export function hausnummerImStrassenfeld(strasse: string | null | undefined): boolean {
  const wert = strasse?.trim() ?? ''
  return /\s\d+\s?[a-zA-Z]?\.?$/.test(wert) || /(str\.|straße|strasse)\s?\d+\s?[a-zA-Z]?$/i.test(wert)
}

/**
 * Die beiden Pruefregeln. Gehoeren INS Schema (Repo-Regel: nur dann faerbt Material das Feld rot),
 * also im Schema-Aufruf des jeweiligen Formulars aufrufen, je Adresse einmal.
 */
export function strasseHausnummerRegeln(strasse: SchemaPath<string>, hausnummer: SchemaPath<string>): void {
  required(hausnummer, {
    message: HAUSNUMMER_PFLICHT_MELDUNG,
    when: (ctx) => (ctx.valueOf(strasse) ?? '').trim() !== '',
  })
  validate(strasse, (ctx) =>
    hausnummerImStrassenfeld(ctx.value())
      ? { kind: 'hausnummer-im-strassenfeld', message: HAUSNUMMER_IM_STRASSENFELD_MELDUNG }
      : null,
  )
}
